import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { showError, showNotification } from '@vk-hr-tek/core/notifications';

import {
  CompanyEventsStat,
  CompanyEventTypeItem,
  CompanyGroupUsers,
  DeleteManagerSubstituteRequest,
  GetCompanyEventTypesParams,
  GetManagerSubstitutesListRequest,
  GetManagerSubstitutesQuery,
  SettingsService,
} from '@app/gen/settings';
import { Entity } from '@app/types';

import { ThunkExtra, ThunkValuesAndActions } from '../../../../app/store';
import {
  EventType,
  ManagerSubstitutesEmployee,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';
import { CreateSubstituteDto } from '../../../dto';
import { SettingsDetailMapper } from '../../../mappers';

export const getCompanyEventTypes = createAsyncThunk<
  CompanyEventTypeItem[],
  GetCompanyEventTypesParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getCompanyEventTypes',
  async ({ company_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);
      const result = await settingsService.getCompanyEventTypes({
        params: { company_id },
        query: { has_manager: true },
      });

      return result.event_types;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getManagerSubstitutes = createAsyncThunk<
  {
    substitutes: ManagerSubstitutesEmployee[];
    total: number;
  },
  GetManagerSubstitutesListRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getManagerSubstitutes',
  async (
    { company_id, offset, limit, search_employee },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const settingsService = await inject(SettingsService);
      const result = await settingsService.getManagerSubstitutesList({
        body: {
          company_id,
          offset,
          limit,
          ...(search_employee ? { search_employee } : {}),
        },
      });

      const mapper = await inject(SettingsDetailMapper);
      return {
        substitutes: mapper.processManagerSubstitutes(result.substitutes),
        total: result.total,
      };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getManagerSubstitutesEventType = createAsyncThunk<
  EventType[],
  GetManagerSubstitutesQuery,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getManagerSubstitutesEventType',
  async (
    { employee_id, company_id },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const settingsService = await inject(SettingsService);
      const result = await settingsService.getManagerSubstitutes({
        query: {
          employee_id,
          company_id,
        },
      });
      const mapper = await inject(SettingsDetailMapper);
      return mapper.processManagerSubstitutesEventType(result.event_types);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const removeSubstitute = createAsyncThunk<
  void,
  ThunkValuesAndActions<
    DeleteManagerSubstituteRequest & { company_id: string }
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/removeSubstitute',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).deleteManagerSubstitute({
        body: {
          employee_id: values.employee_id,
          substitute_id: values.substitute_id,
          scope: values.scope,
          event_type_id: values.event_type_id,
          dates: values.dates,
        },
      });

      dispatch(showNotification('Заместитель успешно удален'));
      actions.resolve(null);
      dispatch(
        getManagerSubstitutesEventType({
          employee_id: values.employee_id,
          company_id: values.company_id,
        }),
      );
    } catch (err) {
      dispatch(showError('При удалении заместителя произошла ошибка'));
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const addSubstitute = createAsyncThunk<
  void,
  ThunkValuesAndActions<CreateSubstituteDto>,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/addSubstitute',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(SettingsService).createManagerSubstitute({
        body: {
          employee_id: values.employeeId,
          event_types: values.eventTypes,
          substitute_id: values.substituteId,
          from_absences: values.isManagerAbsencePeriod,
          ...(!!values.dates?.length ? { dates: values.dates } : {}),
        },
      });

      dispatch(showNotification('Заместитель добавлен'));
      actions.resolve(null);
      dispatch(
        getManagerSubstitutesEventType({
          employee_id: values.employeeId,
          company_id: values.companyId,
        }),
      );
    } catch (err) {
      dispatch(showError('При добавлении заместителя произошла ошибка'));
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsSubstitutesReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getCompanyEventTypes.pending, (state) => {
    state.companyEventTypes = {
      status: 'loading',
      items: [],
      error: null,
    };
  });
  builder.addCase(getCompanyEventTypes.fulfilled, (state, { payload }) => {
    state.companyEventTypes = {
      status: 'complete',
      items: payload,
      error: null,
    };
  });
  builder.addCase(
    getCompanyEventTypes.rejected,
    (state, { payload, error }) => {
      state.companyEventTypes = {
        status: 'failed',
        items: [],
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
      };
    },
  );
  builder.addCase(getManagerSubstitutes.pending, (state) => {
    state.managerSubstitutes = {
      ...state.managerSubstitutes,
      status: 'loading',
      error: null,
    };
  });
  builder.addCase(getManagerSubstitutes.fulfilled, (state, { payload }) => {
    state.managerSubstitutes = {
      ...state.managerSubstitutes,
      status: 'complete',
      items: payload.substitutes,
      total: payload.total,
      error: null,
    };
  });
  builder.addCase(
    getManagerSubstitutes.rejected,
    (state, { payload, error }) => {
      state.managerSubstitutes = {
        ...state.managerSubstitutes,
        status: 'failed',
        items: [],
        total: 0,
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
      };
    },
  );
  builder.addCase(getManagerSubstitutesEventType.pending, (state, { meta }) => {
    state.eventTypes[meta.arg.employee_id] = {
      status: 'loading',
      error: null,
      types: [],
    };
  });
  builder.addCase(
    getManagerSubstitutesEventType.fulfilled,
    (state, { payload, meta }) => {
      state.eventTypes[meta.arg.employee_id] = {
        status: 'complete',
        error: null,
        types: payload.map(({ substitutes, eventType }) => ({
          eventType,
          substitutes: [...substitutes].sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        })),
      };
    },
  );
  builder.addCase(
    getManagerSubstitutesEventType.rejected,
    (state, { payload, error, meta }) => {
      state.eventTypes[meta.arg.employee_id] = {
        status: 'failed',
        error:
          payload ||
          ({
            info: (error && error.message) || 'Unknown error',
            status: 500,
            source: 'client',
            title: 'Internal client error',
          } as AppError),
        types: [],
      };
    },
  );
};
